import moment from "moment";
import parse from "html-react-parser";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { FULL_DATE_FORMAT } from "../../constants/Constants";
import Localize from "../../locale/Localize";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { toggleMessageOriginal } from "../../reducers/chat-reducer";

const getParticipantName = (chat, isSender) => {
    const participantName = GLOBAL_MEETING_DATA.isAgent ? CALL_DATA.participantUserName : chat.guestName;
    return isSender ? `${participantName} ${Localize[PublicVariables.currentLanguageObj.languageCode]["you"]}` : chat.guestName;
};

const getShortName = (name) =>
    name
        .split(" ")
        .map((word) => word?.[0]?.toUpperCase())
        .slice(0, 2) // Take only the first two characters
        .join("");

const formattedDate = (chat) => moment.utc(chat.date_sent, FULL_DATE_FORMAT).local().format("hh:mm A");

export const ChatBox = ({ chat, isSender }) => {
    const dispatch = useDispatch();
    const localisedValues = Localize[PublicVariables.currentLanguageObj.languageCode];

    // Determine which text to display
    // For agent's sent translated messages: show originalText when showOriginal is true
    // For received translated messages: show translatedText when showOriginal is false
    const getDisplayText = () => {
        // Agent's translated outgoing message
        if (isSender && chat.isAgentTranslatedMessage) {
            return chat.showOriginal ? chat.originalText : chat.translatedText;
        }
        // Incoming translated message
        if (chat.translatedText && !chat.showOriginal) {
            return chat.translatedText;
        }
        return chat.textMsg;
    };

    const displayText = getDisplayText();

    // Check if translation toggle should be shown
    const showTranslationToggle =
        !chat.isTranslating && ((isSender && chat.isAgentTranslatedMessage && chat.originalText) || (!isSender && chat.translatedText));

    const handleToggleTranslation = () => {
        dispatch(toggleMessageOriginal({ messageId: chat.messageId }));
    };

    return (
        <div className={clsx("chat-message-box", isSender ? "send-message" : "receive-message")}>
            <div className='chat-message-icon'>
                <div className='for-short-name'>
                    {getShortName(isSender && GLOBAL_MEETING_DATA.isAgent ? CALL_DATA.participantUserName : chat.guestName)}
                </div>
            </div>
            <div className='chat-message-text'>
                <div className='chat-message-text-in'>
                    <div className='chat-message-info'>
                        <div className='chat-message-info-box'>
                            <div className='info-text'>{getParticipantName(chat, isSender)}</div>
                        </div>
                        <div className='chat-message-info-box chat-message-time'>
                            <div className='info-text'>{formattedDate(chat)}</div>
                        </div>
                    </div>
                    <div className='chat-message-view'>
                        <span className='chat-message-view-text'>{parse(displayText)}</span>
                        {/* Translation indicator */}
                        {chat.isTranslating && (
                            <div className='chat-translation-status'>
                                <span className='translating-indicator'>{localisedValues["translating"]}</span>
                            </div>
                        )}
                    </div>
                </div>
                {/* Translation toggle - outside message box with icon */}
                {showTranslationToggle && (
                    <div className='chat-translation-toggle-wrapper'>
                        <button className='translation-toggle-btn' onClick={handleToggleTranslation} type='button'>
                            <span className='translate-icon-text'>{`A\u6587`}</span>
                            <span className='toggle-text'>
                                {chat.showOriginal ? localisedValues["show_translation"] : localisedValues["show_original"]}
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

ChatBox.propTypes = {
    chat: PropTypes.object,
    isSender: PropTypes.bool,
};
